import React, { useState, useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { pdf } from '@react-pdf/renderer';
import { Download, Eye, Loader2 } from 'lucide-react';
import { purchaseOrderSchema, type PurchaseOrderFormData, type PurchaseOrder } from '../../types/purchaseOrder';
import { inventoryService } from '../../services/inventoryService';
import { supplierService } from '../../services/supplierService';
import api from '../../services/api';
import { CreateInventoryModal } from '../../components/inventory/CreateInventoryModal';
import { useSettingsStore } from '../../store/settingsStore';
import { useAuthStore } from '../../store/authStore';
import { useCompanyLogo } from '../../hooks/useCompanyLogo';
import { Button } from '../../components/common/Button';
import { PurchaseOrderInvoicePDF } from '../../components/purchase-orders/PurchaseOrderInvoicePDF';
import type { InventoryItem } from '../../types/inventory';
import type { Supplier } from '../../types/supplier';
import { useToast } from '../../components/common/Toast';
import { POPaymentDetails } from '../../components/purchasing/POPaymentDetails';
import { PODeliveryDetails } from '../../components/purchasing/PODeliveryDetails';
import { POItemsList } from '../../components/purchasing/POItemsList';
import { formatCurrency } from '../../utils/format';
import type { CreateGreenBeanProfileDTO } from '../../types/greenBean';
import { useLanguage } from '../../features/language/useLanguage';
import { POGeneralInfoSection } from './components/createForm/POGeneralInfoSection';
import { HighShrinkageWarningModal } from './components/createForm/HighShrinkageWarningModal';
import { UnitPriceAdjustmentModal, type PriceAdjustmentItem } from './components/createForm/UnitPriceAdjustmentModal';
import { useOnboardingStore } from '../../store/onboardingStore';

interface Props {
  initialData?: PurchaseOrderFormData;
  onSubmit: (data: PurchaseOrderFormData) => Promise<void>;
  onCancel: () => void;
  mode?: 'create' | 'receive' | 'pay';
  readOnly?: boolean;
}

const isLikelyUuid = (value: string): boolean =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);

const createEmptyGreenBeanProfile = (): CreateGreenBeanProfileDTO => ({
  profile_code: '',
  profile_name: '',
  moisture_content: 0,
  color: '',
  grade: '',
  sample_physical_check: null,
  aroma: [],
  country: '',
  province: '',
  region: '',
  farm_name: '',
  producer: '',
  species: '',
  variety: '',
  harvest_date: new Date().getFullYear().toString(),
  origin_notes: '',
  processor: '',
  wet_mill: '',
  dry_mill: '',
  processing_other: '',
  process_type: 'Other',
  processing_notes: '',
  lot_id: undefined,
  roast_id: undefined,
  origin_details: '',
  taste_profile: '',
  qc_cupping: 'Simplified',
  inventory_item_id: undefined,
  sample_id: undefined,
  type: 'ItemType',
});

export const CreatePurchaseOrderForm: React.FC<Props> = ({ initialData, onSubmit, onCancel, mode = 'create', readOnly = false }) => {
  const { t } = useLanguage();
  const { showToast } = useToast();
  // The sample-only pickers belong to the guided purchase (step 2) and to nothing
  // else. Gating them on "a tour is running" hid the user's own suppliers and
  // inventory on every other step, so a purchase order raised for real stock
  // mid-tour could not find the item it was for. sampleRefs holds the exact
  // sample ids.
  const onboardingSamplePickers = useOnboardingStore(
    (s) => s.state?.status === 'in_progress' && s.state?.currentStep === 2,
  );
  const sampleRefs = useOnboardingStore((s) => s.state?.sampleRefs);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const { currencySymbol } = useSettingsStore();
  const { company, user: authUser } = useAuthStore();
  const { data: logoDataUrl } = useCompanyLogo();
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(false);
  const [isCreateItemModalOpen, setIsCreateItemModalOpen] = useState(false);
  const [creatingItemIndex, setCreatingItemIndex] = useState<number | null>(null);
  const [createItemInitialName, setCreateItemInitialName] = useState<string | undefined>(undefined);
  const [shrinkageWarningOpen, setShrinkageWarningOpen] = useState(false);
  const [pendingSubmissionData, setPendingSubmissionData] = useState<PurchaseOrderFormData | null>(null);
  const [highShrinkageItems, setHighShrinkageItems] = useState<{name: string, shrinkage: number}[]>([]);
  const [unitPriceAdjustmentModalOpen, setUnitPriceAdjustmentModalOpen] = useState(false);
  const [unitPriceAdjustmentItems, setUnitPriceAdjustmentItems] = useState<PriceAdjustmentItem[]>([]);
  const [isPreviewingPo, setIsPreviewingPo] = useState(false);
  const [isDownloadingPo, setIsDownloadingPo] = useState(false);
  const resolveWeightReceived = React.useCallback((item: PurchaseOrderFormData['items'][number]) => {
    if (mode !== 'receive') return item.weightReceived;
    const receivedWeight = Number(item.weightReceived);
    return Number.isFinite(receivedWeight) && receivedWeight > 0 ? receivedWeight : item.quantity;
  }, [mode]);

  const { register, control, handleSubmit, watch, setValue, setError, formState: { errors, isSubmitting } } = useForm<PurchaseOrderFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(purchaseOrderSchema) as any,
    mode: 'onChange',
    shouldUnregister: false,
    defaultValues: initialData ? {
      ...initialData,
      items: initialData.items.map(item => ({
        ...item,
        sampleItem: item.sampleItem ?? false,
        weightReceived: resolveWeightReceived(item),
      }))
    } : {
      supplier: '',
      orderDate: new Date().toISOString().split('T')[0],
      expectedDeliveryDate: new Date(new Date().setDate(new Date().getDate() + 1)).toISOString().split('T')[0],
      status: 'ordered',
      paymentStatus: 'paid',
      paymentType: t('purchaseOrder.filters.options.bankTransfer'),
      deliveryStatus: 'received',
      internalNotes: '',
      items: [{ inventoryItemId: '', quantity: 0, unitPrice: 0, name: '', weightReceived: 0, sampleItem: false }]
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items'
  });

  const watchedSupplier = watch('supplier');
  const watchedItems = watch('items');
  const watchedDeliveryStatus = watch('deliveryStatus');
  const watchedPaymentType = watch('paymentType');
  const isReceiveMode = mode === 'receive' || watchedDeliveryStatus === 'received';
  const isSaveDisabled =
    (isReceiveMode && watchedItems?.some(item => !item.weightReceived || item.weightReceived <= 0)) ||
    !watchedPaymentType ||
    watchedPaymentType.trim() === '' ||
    watchedItems?.some(item => (item.unitPrice || 0) === 0) ||
    watchedItems?.some(item => item.weightReceived != null && item.weightReceived > (item.quantity || 0));
  const isEditMode = Boolean(initialData);
  const submitButtonText = mode === 'create'
    ? (isEditMode ? t('purchaseOrder.form.submit.save') : t('purchaseOrder.form.submit.create'))
    : t('purchaseOrder.form.submit.save');

  const [supplierInternalNotes, setSupplierInternalNotes] = useState('');
  const [selectedSupplierId, setSelectedSupplierId] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedSupplierId) {
      const raw = (watchedSupplier || '').trim();
      if (!raw) setSupplierInternalNotes('');
      return;
    }
    const supplier = suppliers.find(s => s.id === selectedSupplierId);
    setSupplierInternalNotes(supplier?.internalNotes || '');
  }, [selectedSupplierId, watchedSupplier, suppliers]);
  
  const loadData = React.useCallback(async () => {
    try {
      const [invData, supData] = await Promise.all([
        inventoryService.getAll({ includeOnboardingSamples: onboardingSamplePickers }),
        supplierService.getAll({ bypassCache: onboardingSamplePickers })
      ]);
      const visibleItems = onboardingSamplePickers ? invData.filter(i => i.isOnboardingSample) : invData;
      setInventoryItems(visibleItems.sort((a, b) => a.name.localeCompare(b.name)));
      setSuppliers(onboardingSamplePickers ? supData.filter(s => s.id === sampleRefs?.supplierId) : supData);

      // If editing, map supplier ID to Name for display
      if (initialData?.supplier) {
        const found = supData.find(s => s.id === initialData.supplier);
        if (found) {
          setValue('supplier', found.name);
          setSelectedSupplierId(found.id);
        }
      }

      // If editing, map item IDs to Name (Code) format for display
      if (initialData?.items) {
        const updatedItems = initialData.items.map(item => {
          const foundInv = invData.find(inv => inv.id === item.inventoryItemId);
          return {
            ...item,
            name: foundInv ? `${foundInv.name} (${foundInv.code})` : item.name,
            itemCode: foundInv ? foundInv.code : item.itemCode,
            unitPrice: item.unitPrice !== undefined && item.unitPrice !== null ? item.unitPrice : (foundInv ? (foundInv.standardCost || 0) : 0),
            weightReceived: resolveWeightReceived(item),
          };
        });
        setValue('items', updatedItems);
      }
    } catch (err) {
      console.error('Failed to load data', err);
      setIsError(true);
    }
  }, [initialData, resolveWeightReceived, setValue, onboardingSamplePickers, sampleRefs]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const processSubmission = async (data: PurchaseOrderFormData) => {
    setLoading(true);
    setIsError(false);
    try {
      // Find supplier by name or create new
      let supplierId = '';
      const typedSupplier = data.supplier.trim();
      const normalizedSupplierName = typedSupplier.toLowerCase();
      // The supplier field accepts free text, and both codes and ids leak into it
      // (codes are printed in the supplier list and inside every PO number; an id
      // stays in the field when editing a PO whose supplier list failed to load).
      // Resolving all three keeps that text from becoming a new supplier named
      // after a code or a UUID.
      const existingSupplier =
        suppliers.find(s => s.name.trim().toLowerCase() === normalizedSupplierName) ||
        suppliers.find(s => (s.code || '').trim().toLowerCase() === normalizedSupplierName) ||
        suppliers.find(s => s.id === typedSupplier);
      if (!existingSupplier && isLikelyUuid(typedSupplier)) {
        setError('supplier', { type: 'manual', message: t('purchaseOrder.form.errors.supplierUnresolved') });
        showToast(t('purchaseOrder.form.toast.supplierUnresolved'), 'error');
        return;
      }
      if (mode === 'create' && existingSupplier?.status === 'Inactive') {
        setError('supplier', { type: 'manual', message: t('purchaseOrder.form.errors.supplierInactive') });
        showToast(t('purchaseOrder.form.toast.supplierInactive'), 'error');
        return;
      }
      
      if (existingSupplier) {
        if ((existingSupplier.internalNotes || '') !== (supplierInternalNotes || '')) {
          const updated = await supplierService.update(existingSupplier.id, {
            ...existingSupplier,
            internalNotes: supplierInternalNotes,
          });
          setSuppliers(prev => prev.map(s => (s.id === updated.id ? updated : s)));
        }
        supplierId = existingSupplier.id;
      } else {
        const newSupplier = await supplierService.create({
          name: typedSupplier,
          status: 'Active',
          internalNotes: supplierInternalNotes,
        });
        supplierId = newSupplier.id;
        // The backend resolves the name against existing suppliers, so this can
        // come back as a row already in the list.
        setSuppliers(prev => prev.some(s => s.id === newSupplier.id)
          ? prev.map(s => (s.id === newSupplier.id ? newSupplier : s))
          : [...prev, newSupplier]);
      }

      // Ensure names are populated if missing (though they should be set on select)
      // And create new items if they don't have an ID
      const enrichedItems = await Promise.all(data.items.map(async (item) => {
        if (item.inventoryItemId) {
          const invItem = inventoryItems.find(i => i.id === item.inventoryItemId);
          return {
            ...item,
            name: invItem ? invItem.name : item.name || t('purchaseOrder.form.values.unknownItem')
          };
        } else if (item.name) {
          // Create new item
          try {
            const newItem = await inventoryService.create({
              name: item.name,
              code: undefined,
              unitOfMeasure: 'kg',
              itemType: 'green_bean',
              reorderPoint: 0,
              reorderQuantity: 0,
              standardCost: item.unitPrice,
              green_bean_profile: createEmptyGreenBeanProfile(),
            } as never);
            
            return {
              ...item,
              inventoryItemId: newItem.id,
              itemCode: newItem.code
            };
          } catch (error) {
            console.error("Failed to create new item:", error);
            throw new Error(`Failed to create item: ${item.name}`);
          }
        }
        return item;
      }));

      // Submit with ID instead of Name
      const submissionData = { 
        ...data, 
        supplier: supplierId,
        items: enrichedItems 
      };

      // Sanitize data based on status
      if (submissionData.deliveryStatus !== 'received') {
        submissionData.actualDeliveryDate = undefined;
        submissionData.items = submissionData.items.map(item => ({
          ...item,
          weightReceived: undefined,
          // Clear lot info if not received
          lotId: undefined,
          lotNumber: undefined
        }));
      }

      await onSubmit(submissionData);

      // Remove the old payment proof file from the server if it was removed or changed
      if (initialData?.paymentProofUrl && initialData.paymentProofUrl !== submissionData.paymentProofUrl) {
         try {
           const filename = initialData.paymentProofUrl.split('/').pop();
           if (filename) await api.delete(`/uploads/documents/${filename}`);
         } catch (err) {
           console.error("Failed to delete old payment proof from storage:", err);
         }
      }

      // Check if we need to show specific success message for samples
      if (submissionData.deliveryStatus === 'received' && submissionData.items.some(i => i.sampleItem)) {
        showToast(t('purchaseOrder.form.toast.receivedSuccess'), 'success');
      } else {
        showToast(t('purchaseOrder.form.toast.savedSuccess'), 'success');
      }

      setIsSuccess(true);
      setTimeout(() => {
        setIsSuccess(false);
        onCancel();
      }, 1000);
    } catch (error) {
      console.error("Error submitting form", error);
      showToast(t('purchaseOrder.form.toast.saveFailed'), 'error');
      setIsError(true);
      setTimeout(() => setIsError(false), 2000);
    } finally {
      setLoading(false);
    }
  };

  const getUnitPriceAdjustmentItems = (data: PurchaseOrderFormData): PriceAdjustmentItem[] => {
    return data.items.reduce((acc, item, index) => {
      const orderedWeight = Number(item.quantity) || 0;
      const receivedWeight = Number(item.weightReceived) || 0;
      const originalUnitPrice = Number(item.unitPrice) || 0;
      const inventoryName = item.inventoryItemId
        ? inventoryItems.find(inv => inv.id === item.inventoryItemId)?.name
        : undefined;
      const fallbackName = item.itemCode ? t('purchaseOrder.form.values.itemWithCode', { code: item.itemCode }) : t('purchaseOrder.form.values.unknownItem');
      const rawName = (item.name || '').trim();
      const displayName = inventoryName || (!rawName || isLikelyUuid(rawName) ? fallbackName : rawName);

      if (orderedWeight > receivedWeight && receivedWeight > 0) {
        const totalPayment = orderedWeight * originalUnitPrice;
        const adjustedUnitPrice = Math.round((totalPayment / receivedWeight) * 1000) / 1000;
        acc.push({
          index,
          name: displayName,
          orderedWeight,
          receivedWeight,
          originalUnitPrice,
          adjustedUnitPrice
        });
      }

      return acc;
    }, [] as PriceAdjustmentItem[]);
  };

  const applyAdjustedUnitPrices = (data: PurchaseOrderFormData, adjustments: PriceAdjustmentItem[]): PurchaseOrderFormData => {
    const adjustedPriceByIndex = new Map(adjustments.map(item => [item.index, item.adjustedUnitPrice]));
    const nonLossExpenses = (data.purchaseExpenses || []).filter(expense => expense.expenseType !== 'po_weight_loss');
    return {
      ...data,
      items: data.items.map((item, index) => ({
        ...item,
        unitPrice: adjustedPriceByIndex.get(index) ?? item.unitPrice
      })),
      purchaseExpenses: nonLossExpenses
    };
  };

  const withKeepOriginalLossExpenses = (
    data: PurchaseOrderFormData,
    adjustments: PriceAdjustmentItem[]
  ): PurchaseOrderFormData => {
    if (adjustments.length === 0) return data;

    const retainedExpenses = (data.purchaseExpenses || []).filter(expense => expense.expenseType !== 'po_weight_loss');
    const lossExpenses = adjustments.map((item) => {
      const weightDifference = Math.round((item.orderedWeight - item.receivedWeight) * 1000) / 1000;
      const lossAmount = Math.round(weightDifference * item.originalUnitPrice * 100) / 100;
      return {
        expenseType: 'po_weight_loss',
        itemId: data.items[item.index]?.inventoryItemId,
        itemCode: data.items[item.index]?.itemCode,
        itemName: item.name,
        quantityLoss: weightDifference,
        unitPrice: item.originalUnitPrice,
        amount: lossAmount,
        description: t('purchaseOrder.form.values.lossDescription', {
          itemName: item.name,
          weightDifference: weightDifference.toFixed(3),
          unitPrice: formatCurrency(item.originalUnitPrice, currencySymbol),
        })
      };
    });

    return {
      ...data,
      purchaseExpenses: [...retainedExpenses, ...lossExpenses]
    };
  };

  const handleFormSubmit = async (data: PurchaseOrderFormData) => {
    if (data.actualDeliveryDate && data.orderDate && data.actualDeliveryDate < data.orderDate) {
      setError('actualDeliveryDate', {
        type: 'manual',
        message: t('purchaseOrder.form.errors.actualDeliveryDateInvalid'),
      });
      showToast(t('purchaseOrder.form.errors.actualDeliveryDateInvalid'), 'error');
      return;
    }

    // Validation for receive process
    if (mode === 'receive' || data.deliveryStatus === 'received') {
      // 1. Check if arrival weight is filled
      const hasMissingWeight = data.items.some(item => !item.weightReceived || item.weightReceived <= 0);
      if (hasMissingWeight) {
        showToast(t('purchaseOrder.form.errors.arrivalWeightRequired'), 'error');
        return;
      }

      // 2. Check for high shrinkage (> 30%)
      const riskyItems = data.items.reduce((acc, item) => {
        const ordered = item.quantity || 0;
        const received = item.weightReceived || 0;
        if (ordered > 0) {
          const shrinkage = ((ordered - received) / ordered) * 100;
          if (shrinkage > 30) {
            acc.push({ name: item.name, shrinkage });
          }
        }
        return acc;
      }, [] as { name: string; shrinkage: number }[]);

      if (riskyItems.length > 0) {
        setHighShrinkageItems(riskyItems);
        setPendingSubmissionData(data);
        setShrinkageWarningOpen(true);
        return;
      }

      const itemsToAdjust = getUnitPriceAdjustmentItems(data);
      if (itemsToAdjust.length > 0) {
        setPendingSubmissionData(data);
        setUnitPriceAdjustmentItems(itemsToAdjust);
        setUnitPriceAdjustmentModalOpen(true);
        return;
      }
    }

    await processSubmission(data);
  };

  const buildPurchaseOrderPdfDocument = React.useCallback(() => {
    if (!initialData || !('id' in initialData) || !(initialData as PurchaseOrder).id) {
      return null;
    }

    return (
      <PurchaseOrderInvoicePDF
        order={{
          ...(initialData as PurchaseOrder),
          ...watch(),
          id: (initialData as PurchaseOrder).id,
          orderNumber: (initialData as PurchaseOrder).orderNumber || 'DRAFT',
          totalAmount: (initialData as PurchaseOrder).totalAmount ?? 0,
          createdAt: (initialData as PurchaseOrder).createdAt || new Date().toISOString(),
          updatedAt: (initialData as PurchaseOrder).updatedAt || new Date().toISOString(),
          supplierName:
            suppliers.find((s) => s.id === selectedSupplierId)?.name ||
            (initialData as PurchaseOrder).supplierName,
        } as PurchaseOrder}
        companyInfo={
          company
            ? {
                ...company,
                email: company.email || authUser?.email,
                phone: company.phone,
                logoDataUrl: logoDataUrl || null,
              }
            : null
        }
        supplierInfo={(() => {
          const supplier = suppliers.find((s) => s.id === selectedSupplierId);
          if (supplier) {
            return {
              name: supplier.name,
              email: supplier.email || undefined,
              phone: supplier.phone,
              contactName: supplier.contactName,
            };
          }
          const typedName = (watch('supplier') || '').trim();
          return typedName ? { name: typedName } : null;
        })()}
        currencySymbol={currencySymbol}
      />
    );
  }, [authUser?.email, company, currencySymbol, initialData, logoDataUrl, selectedSupplierId, suppliers, watch]);

  const getPurchaseOrderFileName = React.useCallback(
    () => `PO-${(initialData as PurchaseOrder | undefined)?.orderNumber || 'DRAFT'}.pdf`,
    [initialData]
  );

  const handlePreviewPurchaseOrder = React.useCallback(async () => {
    const pdfDocument = buildPurchaseOrderPdfDocument();
    if (!pdfDocument || isPreviewingPo) return;

    const previewWindow = window.open('', '_blank');
    setIsPreviewingPo(true);

    try {
      const blob = await pdf(pdfDocument).toBlob();
      const url = URL.createObjectURL(blob);
      if (previewWindow) {
        previewWindow.location.href = url;
      } else {
        window.open(url, '_blank');
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      console.error('Failed to preview purchase order PDF', error);
      if (previewWindow) previewWindow.close();
    } finally {
      setIsPreviewingPo(false);
    }
  }, [buildPurchaseOrderPdfDocument, isPreviewingPo]);

  const handleDownloadPurchaseOrder = React.useCallback(async () => {
    const pdfDocument = buildPurchaseOrderPdfDocument();
    if (!pdfDocument || isDownloadingPo) return;

    setIsDownloadingPo(true);
    try {
      const blob = await pdf(pdfDocument).toBlob();
      const url = URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = getPurchaseOrderFileName();
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 5_000);
    } catch (error) {
      console.error('Failed to download purchase order PDF', error);
    } finally {
      setIsDownloadingPo(false);
    }
  }, [buildPurchaseOrderPdfDocument, getPurchaseOrderFileName, isDownloadingPo]);

  return (
    <>
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
        <div className="space-y-8">
        {/* General Information Section */}
        {mode === 'create' && (
          <POGeneralInfoSection
            control={control}
            register={register}
            errors={errors}
            readOnly={readOnly}
            suppliers={suppliers}
            supplierInternalNotes={supplierInternalNotes}
            onSupplierInternalNotesChange={setSupplierInternalNotes}
            onSupplierSelect={setSelectedSupplierId}
            statusDisabled={initialData?.status === 'cancelled' || readOnly}
          />
        )}

        <div className={`grid grid-cols-1 gap-6 ${mode === 'create' ? 'md:grid-cols-2' : ''}`}>
          {/* Delivery Section */}
          {(mode === 'create' || mode === 'receive') && (
          <PODeliveryDetails
            register={register}
            watch={watch}
            setValue={setValue}
            errors={errors}
            initialStatus={initialData?.deliveryStatus}
            readOnly={readOnly}
          />
          )}

          {/* Payment Section */}
          {(mode === 'create' || mode === 'pay') && (
          <POPaymentDetails
            control={control}
            register={register}
            setValue={setValue}
            errors={errors}
            readOnly={readOnly}
          />
          )}
        </div>
      </div>

      {(mode === 'create' || mode === 'receive') && (
      <POItemsList
        control={control}
        register={register}
        errors={errors}
        watch={watch}
        setValue={setValue}
        fields={fields}
        append={append}
        remove={remove}
        inventoryItems={inventoryItems}
        currencySymbol={currencySymbol}
        setCreatingItemIndex={setCreatingItemIndex}
        setIsCreateItemModalOpen={setIsCreateItemModalOpen}
        setCreateItemInitialName={setCreateItemInitialName}
        mode={mode === 'receive' ? 'receive' : 'edit'}
        isPersistedOrder={Boolean(initialData)}
        readOnly={readOnly}
        disableItemCreation={onboardingSamplePickers}
      />
      )}

      {mode === 'create' && (
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
        <label className="block text-sm font-medium text-gray-700">{t('purchaseOrder.form.fields.internalNotes')}</label>
        <textarea
          data-test-id="po-create-internal-notes"
          {...register('internalNotes')}
          rows={2}
          disabled={readOnly}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 disabled:bg-gray-100 disabled:text-gray-500"
          placeholder={t('purchaseOrder.form.fields.internalNotesPlaceholder')}
        />
      </div>
      )}


      <div className="flex justify-end gap-2 pt-4">
        {initialData && 'id' in initialData && (initialData as PurchaseOrder).id && (
          <div className="inline-flex rounded-md shadow-sm">
            <button
              type="button"
              onClick={handlePreviewPurchaseOrder}
              disabled={isPreviewingPo || isDownloadingPo}
              title={t('purchaseOrder.form.submit.previewPo')}
              aria-label={t('purchaseOrder.form.submit.previewPo')}
              className="inline-flex items-center rounded-l-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            >
              {isPreviewingPo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
            </button>
            <button
              type="button"
              onClick={handleDownloadPurchaseOrder}
              disabled={isPreviewingPo || isDownloadingPo}
              title={t('purchaseOrder.form.submit.downloadPo')}
              aria-label={t('purchaseOrder.form.submit.downloadPo')}
              className="-ml-px inline-flex items-center rounded-r-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            >
              <Download className="mr-2 h-4 w-4" />
              {isDownloadingPo ? t('purchaseOrder.form.submit.preparing') : t('purchaseOrder.form.submit.downloadPo')}
            </button>
          </div>
        )}
        <button type="button" data-test-id="po-create-cancel" onClick={onCancel} className="px-4 py-2 border rounded-md">
          {readOnly ? t('inventory.modal.actions.close') : t('auth.common.cancel')}
        </button>
        {!readOnly && (
          <Button 
            data-test-id="po-create-submit"
            type="submit" 
            isLoading={isSubmitting || loading}
            disabled={isSaveDisabled}
            isSuccess={isSuccess}
            isError={isError}
          >
            {submitButtonText}
          </Button>
        )}
      </div>

      </form>

      <CreateInventoryModal
        isOpen={isCreateItemModalOpen}
        onClose={() => {
          setIsCreateItemModalOpen(false);
          setCreatingItemIndex(null);
          setCreateItemInitialName(undefined);
        }}
        initialName={createItemInitialName}
        hideStockCostSection={true}
        onSuccess={(newItem) => {
          setInventoryItems(prev => [...prev, newItem].sort((a, b) => a.name.localeCompare(b.name)));
          
          if (creatingItemIndex !== null) {
            setValue(`items.${creatingItemIndex}.inventoryItemId`, newItem.id, { shouldValidate: true });
            setValue(`items.${creatingItemIndex}.name`, `${newItem.name} (${newItem.code})`);
            setValue(`items.${creatingItemIndex}.unitPrice`, newItem.standardCost || 0);
            setValue(`items.${creatingItemIndex}.itemCode`, newItem.code);
          }
          setCreateItemInitialName(undefined);
          setCreatingItemIndex(null);
        }}
      />

      <HighShrinkageWarningModal
        isOpen={shrinkageWarningOpen}
        items={highShrinkageItems}
        onClose={() => {
          setShrinkageWarningOpen(false);
          setPendingSubmissionData(null);
          setHighShrinkageItems([]);
        }}
        onProceed={() => {
          if (pendingSubmissionData) {
            const itemsToAdjust = getUnitPriceAdjustmentItems(pendingSubmissionData);
            setShrinkageWarningOpen(false);
            setHighShrinkageItems([]);
            if (itemsToAdjust.length > 0) {
              setUnitPriceAdjustmentItems(itemsToAdjust);
              setUnitPriceAdjustmentModalOpen(true);
              return;
            }
            processSubmission(pendingSubmissionData);
            setPendingSubmissionData(null);
          }
        }}
      />

      <UnitPriceAdjustmentModal
        isOpen={unitPriceAdjustmentModalOpen}
        items={unitPriceAdjustmentItems}
        currencySymbol={currencySymbol}
        onClose={() => {
          setUnitPriceAdjustmentModalOpen(false);
          setUnitPriceAdjustmentItems([]);
          setPendingSubmissionData(null);
        }}
        onKeepOriginal={() => {
          if (pendingSubmissionData) {
            const dataWithLossExpenses = withKeepOriginalLossExpenses(pendingSubmissionData, unitPriceAdjustmentItems);
            processSubmission(dataWithLossExpenses);
          }
          setUnitPriceAdjustmentModalOpen(false);
          setUnitPriceAdjustmentItems([]);
          setPendingSubmissionData(null);
        }}
        onApply={() => {
          if (pendingSubmissionData) {
            const adjustedData = applyAdjustedUnitPrices(pendingSubmissionData, unitPriceAdjustmentItems);
            processSubmission(adjustedData);
          }
          setUnitPriceAdjustmentModalOpen(false);
          setUnitPriceAdjustmentItems([]);
          setPendingSubmissionData(null);
        }}
      />
    </>
  );
};
